import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

/**
 * Lightweight first-visit consent bar. Shows once until accepted.
 * Stored server-side via /api/web/consent so it survives device changes.
 *
 * Iter XX.267d — Terms / Privacy / Trust regressed on physical iOS
 * Capacitor builds: `<Link>` renders an `<a href="/terms">` which
 * some WKWebView / gesture-recognizer combinations swallow before
 * React Router can pick it up (the link then feels "dead"). Replacing
 * the anchor with a `<button>` + `useNavigate()` restores the tap
 * reliably on native without changing route destinations or copy.
 */
export default function ConsentBar({ sessionId, onAccepted }) {
  const navigate = useNavigate();
  async function accept() {
    try {
      await api.post("/web/consent", { session_id: sessionId, tos_accepted: true });
      onAccepted?.();
    } catch { /* silent — UI will retry next chat reload */ }
  }
  const legalLinkClass =
    "text-emerald-400 hover:underline underline-offset-2 " +
    "focus:outline-none focus:underline";
  const go = (path) => (e) => {
    // Prevent any accidental default anchor behavior on native WebView
    // shells (Capacitor iOS in particular).
    try { e?.preventDefault?.(); } catch { /* noop */ }
    try { e?.stopPropagation?.(); } catch { /* noop */ }
    navigate(path);
  };
  return (
    <div
      className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4 text-sm leading-relaxed"
      data-testid="consent-bar"
    >
      <div className="text-neutral-200 mb-2">
        Gully is your personal cricket development companion. Your conversations and videos stay
        private — never shared with other users.
      </div>
      <div className="text-neutral-400 text-xs mb-3">
        By continuing, you agree to the{" "}
        <button
          type="button"
          onClick={go("/terms")}
          className={legalLinkClass}
          data-testid="consent-bar-terms"
        >Terms</button>
        {" · "}
        <button
          type="button"
          onClick={go("/privacy")}
          className={legalLinkClass}
          data-testid="consent-bar-privacy"
        >Privacy</button>
        {" · "}
        <button
          type="button"
          onClick={go("/trust")}
          className={legalLinkClass}
          data-testid="consent-bar-trust"
        >Trust</button>.
      </div>
      <button
        onClick={accept}
        className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-sm font-medium"
        data-testid="consent-bar-accept"
      >
        OK, let's go
      </button>
    </div>
  );
}
